import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { api } from "../../lib/api";
import { FileText, RotateCw, Download, ArrowLeft, ExternalLink, ChevronDown, Mail, Eye } from "lucide-react";
import { SERVICES } from "../../data/staticData";
import { FIELD_LABELS as FORM_FIELD_LABELS } from "../../data/serviceForms";
import { useAuth } from "../../lib/auth";
import { API_BASE } from "../../lib/api";

function absoluteFileUrl(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${url}`;
  return url;
}

async function downloadWithAuth(url, originalName) {
  const abs = absoluteFileUrl(url);
  const token = localStorage.getItem("seomonk_token");
  try {
    const res = await fetch(abs, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = originalName || "deliverable";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    window.open(abs, "_blank", "noopener");
  }
}

async function viewWithAuth(url) {
  const abs = absoluteFileUrl(url);
  const token = localStorage.getItem("seomonk_token");
  try {
    const res = await fetch(abs, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("View failed");
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank", "noopener");
  } catch {
    window.open(abs, "_blank", "noopener");
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const day = d.getDate();
  const suffix = ["th", "st", "nd", "rd"][((day % 100) - 20) % 10] || ["th", "st", "nd", "rd"][day] || "th";
  return `${day}${suffix} ${d.toLocaleString("en-US", { month: "long" })} ${d.getFullYear()}`;
}

function dueDate(iso) {
  if (!iso) return "Not Assigned";
  const d = new Date(iso);
  d.setDate(d.getDate() + 14);
  return formatDate(d.toISOString());
}

const FIELD_LABELS = {
  target_url: "Website URL",
  website: "Website URL",
  keywords: "Keywords (max 5)",
  anchor: "Preferred anchor",
  industry: "Industry / niche",
  goals: "Goals & notes",
  business: "Business name",
  founder: "Founder / CEO",
  address: "Full business address",
  phone: "Phone number",
  email: "Listing email address",
  description: "Brief business description",
  country: "Select country",
  headline: "Headline",
  company: "Company",
};

function labelize(key) {
  if (FORM_FIELD_LABELS[key]) return FORM_FIELD_LABELS[key];
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch {
        setOrder({ notFound: true });
      }
    })();
  }, [id]);

  if (!order) return <DashboardLayout><div className="text-sm text-inkSoft">Loading…</div></DashboardLayout>;
  if (order.notFound) return <DashboardLayout><div className="text-sm text-inkSoft">Order not found.</div></DashboardLayout>;

  const service = SERVICES.find((s) => s.slug === order.service_slug);
  const fjId = `FJ${order.order_id.replace(/[^0-9a-f]/gi, "").slice(0, 10).toUpperCase()}`;
  const reqEntries = Object.entries(order.requirements || {});

  return (
    <DashboardLayout>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm" data-testid="order-detail-breadcrumb">
        <Link to="/dashboard/orders" className="text-terra hover:underline inline-flex items-center gap-1"><ArrowLeft size={14} /> Orders</Link>
        <ChevronDown size={12} className="rotate-[-90deg] text-inkSoft" />
        <span className="font-mono text-xs text-inkSoft">{fjId}</span>
      </div>

      {/* Title + actions */}
      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="order-detail-title">{service?.title || order.service_slug}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <button className="inline-flex items-center gap-2 text-ink hover:text-terra transition" data-testid="action-view-invoice"><FileText size={16} /> View Invoice</button>
          <Link to="/dashboard/new-order" className="inline-flex items-center gap-2 text-ink hover:text-terra transition" data-testid="action-order-again"><RotateCw size={16} /> Order Again</Link>
          <button className="inline-flex items-center gap-2 text-ink hover:text-terra transition" data-testid="action-csv"><Download size={16} /> CSV Download</button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="mt-6 rounded-2xl bg-surfaceAlt/60 border border-line px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="order-summary-strip">
        <Field k="Date ordered" v={formatDate(order.created_at)} />
        <Field k="Date due" v={order.status === "pending" ? "Not Assigned" : dueDate(order.created_at)} />
        <Field k="Ordered by" v={<span className="text-terra">{user?.name?.toLowerCase()}</span>} />
        <Field k="Total" v={order.price ? `£${(order.price * 0.79).toFixed(2)} | $${order.price.toFixed(2)}` : "—"} />
      </div>

      {/* Status & progress */}
      <div className="mt-6 rounded-2xl border border-line bg-white p-6 grid md:grid-cols-3 gap-6">
        <div>
          <div className="eyebrow mb-2">Status</div>
          <div className="font-display text-2xl font-semibold capitalize">{order.status.replace("_", " ")}</div>
        </div>
        <div className="md:col-span-2">
          <div className="eyebrow mb-2">Progress</div>
          <div className="w-full h-2 rounded-full bg-surfaceAlt overflow-hidden">
            <div className="h-full bg-terra transition-all" style={{ width: `${order.progress}%` }} />
          </div>
          <div className="mt-2 font-mono text-xs text-inkSoft">{order.progress}% complete</div>
        </div>
      </div>

      {/* Submission table */}
      {reqEntries.length > 0 && (
        <div className="mt-6 rounded-2xl border border-line bg-white overflow-hidden" data-testid="order-submission-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-inkSoft border-b border-line bg-surfaceAlt/40">
                  {reqEntries.map(([k]) => (
                    <th key={k} className="px-5 py-4 font-medium whitespace-nowrap">{labelize(k)}</th>
                  ))}
                  <th className="px-5 py-4 font-medium">Report</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {reqEntries.map(([k, v]) => {
                    const isUrl = typeof v === "string" && /^https?:\/\//.test(v);
                    return (
                      <td key={k} className="px-5 py-5 align-top max-w-[220px]">
                        {isUrl ? (
                          <a href={v} target="_blank" rel="noreferrer" className="text-terra hover:underline break-all inline-flex items-center gap-1">
                            <span className="underline decoration-dotted underline-offset-2">{v}</span>
                          </a>
                        ) : (
                          <div className="text-ink/85 break-words">{String(v)}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-5 py-5">
                    {(() => {
                      const pdf = (order.deliverables || []).find((d) => (d.type || "").toLowerCase() === "pdf") || (order.deliverables || [])[0];
                      if (pdf) {
                        return (
                          <button onClick={() => viewWithAuth(pdf.url)} className="text-terra hover:underline inline-flex items-center gap-1 underline decoration-dotted underline-offset-2" data-testid="view-report">
                            View Report <ExternalLink size={12} />
                          </button>
                        );
                      }
                      return <span className="text-inkSoft text-xs">Pending</span>;
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Deliverables */}
      {(order.deliverables || []).length > 0 && (
        <div className="mt-6 rounded-2xl border border-line bg-white p-6" data-testid="deliverables-section">
          <div className="eyebrow mb-4">Reports & files</div>
          <ul className="space-y-2">
            {order.deliverables.map((d, i) => {
              const isInternal = typeof d.url === "string" && d.url.startsWith("/api/files/");
              const kind = (d.type || "link").toLowerCase();
              return (
                <li key={i} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-line bg-bg" data-testid={`deliverable-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-surfaceAlt grid place-items-center text-terra shrink-0"><FileText size={16} /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{d.label}</div>
                      <div className="text-xs text-inkSoft flex items-center gap-2 flex-wrap">
                        <span className="tag font-mono text-[10px] uppercase">{kind}</span>
                        {d.original_name && <span className="truncate">{d.original_name}</span>}
                        {d.size && <span className="font-mono">{(d.size / (1024 * 1024)).toFixed(2)} MB</span>}
                      </div>
                      {d.note && <div className="text-[11px] text-inkSoft mt-1">{d.note}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isInternal && (kind === "pdf" || kind === "image") && (
                      <button onClick={() => viewWithAuth(d.url)} className="inline-flex items-center gap-1.5 text-sm text-ink hover:text-terra transition" data-testid={`view-report-${i}`}>
                        <Eye size={14} /> View
                      </button>
                    )}
                    {isInternal ? (
                      <button onClick={() => downloadWithAuth(d.url, d.original_name)} className="inline-flex items-center gap-1.5 text-sm text-terra font-medium hover:underline" data-testid={`download-report-${i}`}>
                        <Download size={14} /> Download
                      </button>
                    ) : (
                      <a href={d.url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-terra font-medium hover:underline" data-testid={`open-report-${i}`}>
                        <ExternalLink size={14} /> Open
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Cancel via email banner */}
      {order.status !== "delivered" && order.status !== "cancelled" && (
        <div className="mt-6 rounded-2xl border border-line bg-surfaceAlt/70 p-5 flex flex-wrap items-center gap-3 justify-between" data-testid="cancel-banner">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-white border border-line grid place-items-center text-terra shrink-0"><Mail size={16} /></div>
            <div>
              <div className="text-sm font-medium">Need to cancel or modify this order?</div>
              <div className="text-xs text-inkSoft mt-0.5">Email us with the order number <span className="font-mono">{order.order_id}</span> and we'll take care of it.</div>
            </div>
          </div>
          <a href={`mailto:zenithranking27@gmail.com?subject=Cancel%20order%20${order.order_id}&body=Hi%2C%20I'd%20like%20to%20cancel%20order%20${order.order_id}.`} className="btn-primary text-sm" data-testid="cancel-email-link">
            Email zenithranking27@gmail.com
          </a>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-inkSoft font-mono">{k}</div>
      <div className="font-medium mt-1">{v}</div>
    </div>
  );
}
